import qs from "qs";

// Dedicated client for the fca-filter-ai QA/QC endpoints. Mirrors knowledgeService.js and
// assetTemplateService.js: same baseURL + withCredentials (the session cookie flows on *.fcatracker.com),
// and the same "retry once on a transport glitch" interceptor so the first request after the backend
// restarts (uvicorn --reload) doesn't strand the page.
//
// LOADED WITH A DYNAMIC import() ON FIRST USE, exactly like `imageAnalysis/imageAnalysisService.js` and
// `filterAssistant/news/newsStore.js` — and for the reason their headers spell out. This module used
// `import axios` + `axios.create()` at MODULE SCOPE, which was harmless while only the QA/QC page imported
// it (that page's suite mocks this whole module). FCA-7507 then imported it from the recommendation form,
// so it is now reached by `recommendations/index.jsx` -> `RegionInfo`/`SiteInfo`/`BuildingInfo`/assets, and
// six Otto guard suites stopped LOADING: the ones that mock axios died on `attachOttoAuthRetry` running at
// import time ("Cannot read properties of undefined (reading 'response')" — the mock instance has no
// `interceptors`), and the ones that do not mock it died on axios 1.x being ESM, which CRA's jest does not
// transform. 126 tests silently stopped running while the suite count still read green-ish.
//
// A static import with a lazy create fixes only the first half. A lazy `require("axios")` fixes jest and
// BREAKS THE REAL BROWSER (`axios.create is not a function` under CRA 5 / webpack 5) — see
// `__tests__/axiosLoading.test.js`, which fails the build if that shape reappears. So: dynamic import(),
// resolved on first use. Webpack gives a real ES module; jest imports nothing until a call is made, which
// a suite that merely renders a page never does.
//
// The paths below are deliberately plain string literals on an `...Api` axios instance — that is the call
// style the otto-sync `xrepo-proxy-paths` gate extracts, so these endpoints get checked against the
// deployed nginx allowlist manifest (filter-ai/deploy/frontend-proxy-paths.json) like every other Otto
// endpoint. A path the gate cannot see is a path that silently returns index.html in production. Each call
// therefore binds the client to a local named `qaQcApi` first; calling `(await getClient()).get("/...")`
// directly would be invisible to it.

// A body-less request that lands on a stale keep-alive connection makes a fresh FastAPI worker 422 with a
// TOP-LEVEL "body required" error (loc === ["body"]) — the request never really landed, so replay it once
// on a fresh connection. A FIELD-level error on our own payload (loc === ["body", ...]) is a real bad value
// and must surface to the user.
const isTopLevelBodyMissing = d => Array.isArray(d.loc) && d.loc.length === 1 && d.loc[0] === "body";

const isTransportGlitch = err => {
    if (!err.response) return true; // network error / connection reset — never reached the worker
    if (err.response.status !== 422) return false;
    const detail = err.response.data && err.response.data.detail;
    return Array.isArray(detail) && detail.some(isTopLevelBodyMissing);
};

let clientPromise = null;

const getClient = () => {
    if (!clientPromise) {
        clientPromise = Promise.all([
            import("axios"),
            import("../../config/constants"),
            import("../filterAssistant/core/authRetry")
        ])
            .then(([axiosModule, constants, authRetry]) => {
                const axios = axiosModule.default || axiosModule;
                const client = axios.create({
                    baseURL: constants.FILTER_AI_API_URL,
                    // Longer than the knowledge admin's. /qa_qc/preview applies the real prefilter — it
                    // strips HTML and scans for years across every candidate narrative in the project — so
                    // on a 9,000-recommendation project it legitimately takes several seconds. A short
                    // timeout would abort the very call the page needs before it can offer a run.
                    timeout: 180000,
                    withCredentials: true,
                    // REPEAT array format, matching `fcaFastApi` in authorizationService.js — this backend
                    // is FastAPI too. Axios's default serialises an array as `key[]=a&key[]=b`, which
                    // FastAPI's `List[str] = Query(...)` does not recognise: it would bind None and the
                    // parameter would be SILENTLY IGNORED. That matters here because the only array
                    // parameter is `skip_existing_rules`, so the failure would not be an error — it would
                    // be a run that quietly re-analysed everything the user asked it to skip.
                    paramsSerializer: {
                        serialize: params => qs.stringify(params, { arrayFormat: "repeat", skipNulls: true })
                    }
                });

                client.interceptors.response.use(null, async err => {
                    const cfg = err.config;
                    // GETs replay, and so does the DRAFT CHECK — the one POST here that is safe to repeat.
                    // It writes nothing (the whole draft travels in the body; no row is read or touched),
                    // and a surveyor is standing at Save waiting for it. Without this, one dropped
                    // connection reads as "Otto could not complete validation" and stops the save on a
                    // request that never reached the worker. The backend retries the MODEL 3 times inside
                    // one call; nothing retried the call itself.
                    //
                    // Starting a run is still NOT replayed, even though the backend de-duplicates concurrent
                    // runs per project: a glitch on "start" should surface so the curator knows to check
                    // rather than silently firing a second request whose outcome they never see.
                    const url = (cfg && cfg.url) || "";
                    const replayable =
                        cfg && (cfg.method === "get" || (cfg.method === "post" && url.startsWith("/qa_qc/check_draft")));
                    if (cfg && !cfg._retried && replayable && isTransportGlitch(err)) {
                        cfg._retried = true;
                        return client(cfg);
                    }
                    return Promise.reject(err);
                });

                // ...then the app's shared 401 refresh-and-replay, so an expired token doesn't read as an
                // empty page.
                authRetry.attachOttoAuthRetry(client);
                return client;
            })
            .catch(err => {
                // Do not cache a failed load: a transient chunk-load failure must not break QA/QC for the
                // rest of the session.
                clientPromise = null;
                throw err;
            });
    }
    return clientPromise;
};

// GET /qa_qc/projects — the picker. `q` is a case-insensitive substring match on the project name.
// Returns every project, including ones with no recommendations; the counts are what locate the work.
export const getQaQcProjects = async (params = {}) => {
    const qaQcApi = await getClient();
    return qaQcApi.get("/qa_qc/projects", { params }).then(res => res.data);
};

// GET /qa_qc/rules — the detector vocabulary (key + the exact issue title it stores). Read from the backend
// rather than restated here, so the title the page shows can never drift from the title on a stored issue.
export const getQaQcRules = async () => {
    const qaQcApi = await getClient();
    return qaQcApi.get("/qa_qc/rules").then(res => res.data);
};

// GET /qa_qc/preview — what a run would cover, without spending a model call. `candidates` is the real
// number of model calls: the same prefilter the run uses, MINUS whatever `skip_existing_rules` excludes.
//
// `params.skip_existing_rules` is an array of rule keys; axios serialises it as repeated query params,
// which is what FastAPI's List[str] Query expects. The backend caches the expensive half of this, so
// re-costing after a checkbox toggle is cheap — pass `refresh: true` only to force a fresh scan.
export const getQaQcPreview = async params => {
    const qaQcApi = await getClient();
    return qaQcApi.get("/qa_qc/preview", { params }).then(res => res.data);
};

// GET /qa_qc/cost — what analysing this project has cost so far, across every run. SUPER_ADMIN only; a
// non-admin gets a 403, which callers treat as "no tile" rather than an error.
export const getQaQcCost = async (params = {}) => {
    const qaQcApi = await getClient();
    return qaQcApi.get("/qa_qc/cost", { params }).then(res => res.data);
};

// GET /qa_qc/issues — a project's findings, newest first. `params` may carry
// project_id/status/rule_key/limit/offset; axios drops undefined keys, so callers send only active filters.
export const getQaQcIssues = async (params = {}) => {
    const qaQcApi = await getClient();
    return qaQcApi.get("/qa_qc/issues", { params }).then(res => res.data);
};

// PATCH /qa_qc/issues/{id} — move a finding between pending / resolved / approved.
export const updateQaQcIssueStatus = async (issueId, status) => {
    const qaQcApi = await getClient();
    return qaQcApi.patch(`/qa_qc/issues/${issueId}`, { status }).then(res => res.data);
};

// DELETE /qa_qc/issues/{id} — drop a finding outright (a false positive worth removing, not resolving).
export const deleteQaQcIssue = async issueId => {
    const qaQcApi = await getClient();
    return qaQcApi.delete(`/qa_qc/issues/${issueId}`).then(res => res.data);
};

// POST /qa_qc/issues/mark-moved — record that findings were promoted into Ruby's `recommendation_reviews`.
//
// This does NOT perform the move. The write goes to Ruby's API from moveIssues.js, as the signed-in
// curator; this only records the outcome so the page can show a finding as moved and stop offering it.
// `moves` is `[{ issue_id, review_id }]`, and `review_id` may be null when the recommendation already
// carried an issue with that title (Ruby refuses the duplicate, and the finding is nonetheless there).
//
// The project id travels in the body and scopes the update server-side, for the same reason as the clear
// endpoint: the issue ids come from a selection, and one from another project must stamp nothing.
//
// CHUNKED. The endpoint bounds `moves` at 2000 entries, because each one becomes a bind parameter and
// PostgreSQL accepts at most 32767 of them — so an unbounded list would be a 500 rather than a validation
// error. Sending in batches keeps this caller permanently below that bound no matter how large a selection
// grows, and a 422 here is the one failure worth designing out: the issues are ALREADY in the reviewers'
// queue by the time this runs, so a rejected call leaves them looking unmoved.
//
// Sequential, not concurrent: this is bookkeeping after the work is done, and four parallel writes to the
// same table buy nothing a curator would notice.
const MARK_MOVED_CHUNK = 500;

export const markQaQcIssuesMoved = async (projectId, moves) => {
    const qaQcApi = await getClient();
    const list = Array.isArray(moves) ? moves : [];
    const totals = { marked: 0, marked_ids: [], already_moved: [], not_found: [] };
    for (let i = 0; i < list.length; i += MARK_MOVED_CHUNK) {
        const batch = list.slice(i, i + MARK_MOVED_CHUNK);
        // eslint-disable-next-line no-await-in-loop
        const res = await qaQcApi.post("/qa_qc/issues/mark-moved", { project_id: projectId, moves: batch });
        const data = res.data || {};
        totals.marked += Number(data.marked || 0);
        ["marked_ids", "already_moved", "not_found"].forEach(key => {
            if (Array.isArray(data[key])) totals[key] = totals[key].concat(data[key]);
        });
    }
    return totals;
};

// POST /qa_qc/issues/clear — delete EVERY issue belonging to one project. Irreversible; the page confirms
// first. Returns `{ project_id, deleted }`.
//
// `projectId` is passed in the body and the backend requires it non-empty, so a caller that loses the scope
// gets a 422 rather than a request that deletes something unintended. It is a POST, not a bulk DELETE, for
// the same reason — and note the interceptor above replays only GETs, so a transport glitch on this call
// surfaces instead of silently firing a second delete.
export const clearQaQcIssues = async projectId => {
    const qaQcApi = await getClient();
    return qaQcApi.post("/qa_qc/issues/clear", { project_id: projectId }).then(res => res.data);
};

// POST /qa_qc/runs — start an analysis run; returns the run row to poll. When a run is already in flight
// for this project+rule the backend returns THAT run (200, not an error), so the caller should always read
// the id off the response rather than assuming a new one was created.
export const startQaQcRun = async payload => {
    const qaQcApi = await getClient();
    return qaQcApi.post("/qa_qc/runs", payload).then(res => res.data);
};

// GET /qa_qc/runs/{id} — progress. `stalled` true means the owning pod stopped reporting; treat it as
// finished-abnormally rather than polling forever.
export const getQaQcRun = async runId => {
    const qaQcApi = await getClient();
    return qaQcApi.get(`/qa_qc/runs/${runId}`).then(res => res.data);
};

// GET /qa_qc/runs — recent runs for a project, newest first.
export const getQaQcRuns = async (params = {}) => {
    const qaQcApi = await getClient();
    return qaQcApi.get("/qa_qc/runs", { params }).then(res => res.data);
};

// POST /qa_qc/runs/{id}/cancel — ask a run to stop. Terminal and idempotent.
export const cancelQaQcRun = async runId => {
    const qaQcApi = await getClient();
    return qaQcApi.post(`/qa_qc/runs/${runId}/cancel`).then(res => res.data);
};

// POST /qa_qc/check_draft — the recommendation form's save-time gate (FTO-134). Stateless: the whole draft
// travels in the body because a recommendation being CREATED has no row to point at. Both rules run
// concurrently backend-side, so this is one call's latency (2-6s typical), not one per rule.
//
// The 30s override is deliberate. The module default above is 180s, sized for /qa_qc/preview scanning a
// 9,000-recommendation project; a surveyor waiting to save must not sit on a spinner for three minutes.
// The backend's own deadline is 20s, after which it answers with `failed` rather than hanging, so 30s is
// the transport giving that deadline room rather than a second, competing limit.
//
// Who may call it: the backend's `require_recommendation_author` admits super_admins, users carrying the
// Asset Surveyor flag (`is_surveyor`), and — the one that matters here — anyone holding
// `forms.recommendations.create`, the same permission that puts the New Recommendation button on screen.
// Reading that permission is what keeps this gate and the form agreeing about who writes recommendations.
// There is no Otto grant for this endpoint, and no "surveyor" role: the app has three roles and that is
// not one of them.
//
// So a 403 from here is ANOMALOUS, not routine — the caller reached this code by being on the
// recommendation form, which means they can create one. Callers MUST still fail open (Otto's uptime is
// not the recommendation module's uptime) but must SHOW the refusal rather than swallowing it: a silent
// pass means every recommendation saved during a misconfiguration goes unchecked with nobody able to
// tell.
export const checkQaQcDraft = async payload => {
    const qaQcApi = await getClient();
    return qaQcApi.post("/qa_qc/check_draft", payload, { timeout: 30000 }).then(res => res.data);
};

// --- Recommendation Condition Vocabulary (FTO-145) -------------------------------------------------------------
//
// The settings pages under Project Settings (Master Global + per project) that tune what the QA/QC
// condition rule looks for. Same client, same /qa_qc prefix, same grant as the QA/QC page — `view` reads,
// `manage` writes — so nothing new has to be proxied or granted.

// GET /qa_qc/condition_vocab/bands — the ladder with human labels and descriptions. The page renders THESE
// rather than a copy of its own, so there is no band list to keep in step with the backend.
export const getConditionVocabBands = async () => {
    const qaQcApi = await getClient();
    return qaQcApi.get("/qa_qc/condition_vocab/bands").then(res => res.data);
};

// GET /qa_qc/condition_vocab/entries — the raw rows of ONE scope: `{ scope_type: "global" }` for the
// defaults, `{ scope_type: "project", scope_id }` for a project's own overrides and additions.
export const getConditionVocabEntries = async params => {
    const qaQcApi = await getClient();
    return qaQcApi.get("/qa_qc/condition_vocab/entries", { params }).then(res => res.data);
};

// POST /qa_qc/condition_vocab/entries — create or update ONE placement, keyed on (scope, kind, normalized
// term): saving the same wording twice edits the row. A wording the vocabulary cannot hold is a 400 whose
// `detail` is a sentence to show the curator.
export const saveConditionVocabEntry = async payload => {
    const qaQcApi = await getClient();
    return qaQcApi.post("/qa_qc/condition_vocab/entries", payload).then(res => res.data);
};

// DELETE /qa_qc/condition_vocab/entries/{id} — retire a global default, or drop a project override
// ("use the default" on the project page).
export const deleteConditionVocabEntry = async entryId => {
    const qaQcApi = await getClient();
    return qaQcApi.delete(`/qa_qc/condition_vocab/entries/${encodeURIComponent(entryId)}`).then(res => res.data);
};

// POST /qa_qc/condition_vocab/resolve — how THIS project's vocabulary places each of its condition options
// (the same names the draft check sends), plus the narrative terms in force for it. A POST-shaped READ:
// memory-only on the backend, and what it shows is exactly what the rule will do on the next save.
export const resolveConditionVocab = async (projectId, values) => {
    const qaQcApi = await getClient();
    return qaQcApi
        .post("/qa_qc/condition_vocab/resolve", { project_id: projectId, values: values || [] })
        .then(res => res.data);
};

// GET /qa_qc/condition_vocab/recorded_values — the condition values actually RECORDED on the project's
// recommendations, which is what a run evaluates and which drifts from the dropdown (renamed options,
// legacy values). The project page lists these beside the options so a curator can place a value Otto
// will meet that no surveyor can pick any more.
export const getConditionVocabRecordedValues = async projectId => {
    const qaQcApi = await getClient();
    return qaQcApi
        .get("/qa_qc/condition_vocab/recorded_values", { params: { project_id: projectId } })
        .then(res => res.data);
};
